import { Button } from "@faire/slate/components/button";
import { LoadingSpinner } from "@faire/slate/components/loading-spinner";
import { Radio } from "@faire/slate/components/radio";
import { Core } from "@faire/slate/foundation/color";
import { Column, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import React from "react";

import { INLINE_SPE_COPY } from "./inlineSpeCopy";
import { MockAiSparkleIcon } from "./MockAiSparkleIcon";
import {
  type MockVariantRow,
  MockVariantTableHeader,
  MockVariantTableRow,
} from "./variantTable";

export type OptionsSelection = null | "has" | "no";

export type MockProductOptionsSectionProps = {
  optionsSelection: OptionsSelection;
  onOptionsSelectionChange: (selection: OptionsSelection) => void;
  optionsLoading: boolean;
  showAiBadge: boolean;
  variantRows: MockVariantRow[];
  showVariantPhotos: boolean;
  canRegenerateOptionsFromImages: boolean;
  onRegenerateOptionsFromImages: () => void;
  variantSku: Record<string, string>;
  variantWholesale: Record<string, string>;
  variantRetail: Record<string, string>;
  variantOnHand: Record<string, string>;
  onSkuChange: (key: string, value: string) => void;
  onWholesaleChange: (key: string, value: string) => void;
  onRetailChange: (key: string, value: string) => void;
  onOnHandChange: (key: string, value: string) => void;
};

/**
 * Mirrors SPE `ProductOptionsSection` radios + `MultiOptionProductInfo` table.
 */
export const MockProductOptionsSection: React.FC<
  MockProductOptionsSectionProps
> = ({
  optionsSelection,
  onOptionsSelectionChange,
  optionsLoading,
  showAiBadge,
  variantRows,
  showVariantPhotos,
  canRegenerateOptionsFromImages,
  onRegenerateOptionsFromImages,
  variantSku,
  variantWholesale,
  variantRetail,
  variantOnHand,
  onSkuChange,
  onWholesaleChange,
  onRetailChange,
  onOnHandChange,
}) => {
  const hasMadeSelection = optionsSelection !== null;
  const hasProductOptions = optionsSelection === "has";
  const showVariantTable = hasProductOptions && !optionsLoading;

  return (
    <Column>
      <Row align="flex-start" justify="space-between" gap="8x">
        <Column gap="2x" className="max-w-[640px]">
          <Row align="center" gap="2x">
            <Typography
              variant="subheadingSansMedium"
              color={Core.text.primary}
            >
              {INLINE_SPE_COPY.optionsHeading}
            </Typography>
            <span
              className="text-fs-text-critical type-fs-section-heading"
              aria-hidden
            >
              *
            </span>
            {showAiBadge ? (
              <MockAiSparkleIcon
                tooltipText={INLINE_SPE_COPY.aiOptionsTooltip}
              />
            ) : null}
          </Row>
          <Typography variant="paragraphSansRegular" color={Core.text.subdued}>
            {INLINE_SPE_COPY.optionsSubheading}
          </Typography>
        </Column>
        {showVariantTable ? (
          <Button variant="plain" size="small" type="button">
            {INLINE_SPE_COPY.editProductOptions}
          </Button>
        ) : null}
      </Row>

      <Spacer size="8x" />

      <Column gap="4x">
        <Radio
          id="inline-product-with-options"
          name="inline-product-options"
          label={INLINE_SPE_COPY.radioHasOptions}
          checked={hasMadeSelection && hasProductOptions}
          onChange={() => {
            onOptionsSelectionChange("has");
          }}
        />
        <Radio
          id="inline-product-without-options"
          name="inline-product-options"
          label={INLINE_SPE_COPY.radioNoOptions}
          checked={hasMadeSelection && !hasProductOptions}
          onChange={() => {
            onOptionsSelectionChange("no");
          }}
        />
      </Column>

      {hasProductOptions ? (
        <>
          <Spacer size="6x" />
          {optionsLoading ? (
            <Row align="center" gap="2x" className="py-4">
              <LoadingSpinner size="small" />
              <Typography variant="labelSansRegular" color={Core.text.subdued}>
                {INLINE_SPE_COPY.generatingVariants}
              </Typography>
            </Row>
          ) : null}

          {showAiBadge && (showVariantTable || optionsLoading) ? (
            <>
              <Row
                align="center"
                justify="space-between"
                gap="4x"
                className="flex-wrap"
              >
                <Typography
                  variant="labelSansRegular"
                  color={Core.text.subdued}
                >
                  {INLINE_SPE_COPY.optionsDetectedLead}{" "}
                  <span className="text-fs-text-primary font-semibold">
                    {INLINE_SPE_COPY.optionsDetectedNames}
                  </span>
                </Typography>
                {canRegenerateOptionsFromImages ? (
                  <Button
                    variant="secondary"
                    size="small"
                    type="button"
                    onClick={onRegenerateOptionsFromImages}
                    loading={optionsLoading}
                    disabled={optionsLoading}
                  >
                    {INLINE_SPE_COPY.regenerateOptionsFromImages}
                  </Button>
                ) : null}
              </Row>
              <Spacer size="4x" />
            </>
          ) : null}

          {showVariantTable ? (
            <div className="border-fs-border-muted w-full overflow-x-auto rounded-lg border">
              <div
                className="bg-fs-surface-primary min-w-0"
                role="table"
                aria-label="Product variants"
              >
                <MockVariantTableHeader />
                {variantRows.map((row) => (
                  <MockVariantTableRow
                    key={row.key}
                    row={row}
                    showVariantPhotos={showVariantPhotos}
                    sku={variantSku[row.key] ?? ""}
                    wholesaleDisplay={variantWholesale[row.key] ?? ""}
                    retailDisplay={variantRetail[row.key] ?? ""}
                    onHand={variantOnHand[row.key] ?? ""}
                    onSku={(v) => {
                      onSkuChange(row.key, v);
                    }}
                    onWholesale={(v) => {
                      onWholesaleChange(row.key, v);
                    }}
                    onRetail={(v) => {
                      onRetailChange(row.key, v);
                    }}
                    onOnHand={(v) => {
                      onOnHandChange(row.key, v);
                    }}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </Column>
  );
};
